/**
 * @file OpenTelemetry HTTP Request Start Handler
 * @description Handles the start of an HTTP request by creating a span, logging the request, and recording OpenTelemetry metrics.
 */

import { tracer } from '../../tracers';
import { buildSpanOptionsOfHttpRequest, getIdsFromSpan, setOtToHeaders } from '../../utils';
import { withSafelyExecute } from '../../utils/withSafelyExecute';
import { log } from '../../logging';
import { EVENTS, HTTP_CLIENTS, SEVERITY_TEXT, STAGES } from '../../constants';
import { getDynamicHttpLogAttributes } from '../../utils/getDynamicHttpLogAttributes';
import { buildOTSpanName } from '../../utils/buildOTSpanName';
import { traceContextService } from '../../services/TraceContextService';

/**
 * Handles the start of an HTTP request by creating an OpenTelemetry span, logging the request,
 * adding OpenTelemetry trace_id, span_id to headers, and recording metrics.
 *
 * @param {Object} config - The configuration object for the HTTP request.
 * @param {string} config.url - The URL of the request.
 * @param {string} config.method - The HTTP method of the request (e.g., 'GET', 'POST').
 * @param {Object} [config.headers] - The headers of the request.
 * @param {*} [config.data] - The data payload of the request.
 * @param {Object} [config.logData] - Additional data to include in the log.
 * @param {Object} [config.traceData] - Additional trace-related data for building the span name.
 */
function otlHandleStartRequest(config) {
    const { url, method, headers, data, logData, traceData } = config || {};
    // Build span name based on HTTP request details
    const builtSpanName = buildOTSpanName(traceData, { method, url, data });
    // Create OpenTelemetry span
    config.span = tracer.startSpan(
        builtSpanName,
        buildSpanOptionsOfHttpRequest(url, method, HTTP_CLIENTS.AXIOS),
        traceContextService.getActiveParentContext(),
    );

    // Set OpenTelemetry trace headers in the request
    setOtToHeaders(config, config.span);
    // Log the start of the request
    log(`Starting request to ${url}`, SEVERITY_TEXT.INFO, {
        url,
        method,
        ...getDynamicHttpLogAttributes({ url, stage: STAGES.REQUEST, headers, params: data }),
        ...getIdsFromSpan(config.span),
        ...logData?.attributes,
    });
    // Add an event to the OpenTelemetry span
    config.span.addEvent(EVENTS.REQUEST_STARTED);
}

export default withSafelyExecute(otlHandleStartRequest);
